import { builtInThemeNames, customThemeColors } from './themeRegistry';

export interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  base: string;
}

// TE-63: Tailwind CSS 4/DaisyUI 5 theme definitions live in CSS
// (theme-definitions.css). This static registry (ported from CO, CO-348)
// preserves the Theme Settings swatches without bundling build config into
// the browser -- replaces the old tailwind.config.mjs import, which drifted
// out of sync and (worse) stopped existing at all once CO itself dropped
// that file.
export function getThemeColors(themeName: string): ThemeColors {
  const custom = customThemeColors[themeName as keyof typeof customThemeColors];
  if (custom) return custom;

  // Approximate defaults for daisyUI's own built-in themes, which have no
  // entry of their own in the DaisyUI CSS plugin configuration.
  const defaults: Record<string, ThemeColors> = {
    light: { primary: '#570df8', secondary: '#f000b8', accent: '#37cdbe', base: '#ffffff' },
    dark: { primary: '#661ae6', secondary: '#d926aa', accent: '#1fb2a5', base: '#1d232a' },
    cupcake: { primary: '#65c3c8', secondary: '#ef9fbc', accent: '#eeaf3a', base: '#faf7f5' },
    bumblebee: { primary: '#e0a82e', secondary: '#f9d72f', accent: '#181830', base: '#ffffff' },
    emerald: { primary: '#66cc8a', secondary: '#377cfb', accent: '#ea5234', base: '#ffffff' },
    corporate: { primary: '#4b6bfb', secondary: '#7b92b2', accent: '#67cba0', base: '#ffffff' },
    synthwave: { primary: '#e779c1', secondary: '#58c7f3', accent: '#f3cc30', base: '#1a103d' },
    retro: { primary: '#ef9995', secondary: '#a4cbb4', accent: '#dc8850', base: '#e4d8b4' },
    cyberpunk: { primary: '#ff7598', secondary: '#75d1f0', accent: '#c07826', base: '#ffffff' },
    valentine: { primary: '#e96d7b', secondary: '#a991f7', accent: '#88dbdd', base: '#f8dce5' },
    halloween: { primary: '#f28c18', secondary: '#6d3a9c', accent: '#51a800', base: '#1f1f1f' },
    garden: { primary: '#5c7f67', secondary: '#ecf4e7', accent: '#fc7f7f', base: '#e9e7e7' },
    forest: { primary: '#1eb854', secondary: '#1db88e', accent: '#e8a816', base: '#171212' },
    aqua: { primary: '#09ecf3', secondary: '#966fb3', accent: '#ffe999', base: '#283d3b' },
    lofi: { primary: '#0d0d0d', secondary: '#1a1a1a', accent: '#1a1a1a', base: '#ffffff' },
    pastel: { primary: '#d1c1d7', secondary: '#f9dcc4', accent: '#c5dec9', base: '#ffffff' },
    fantasy: { primary: '#6e0b75', secondary: '#007ebd', accent: '#f8e06f', base: '#ffffff' },
    wireframe: { primary: '#b8b8b8', secondary: '#b8b8b8', accent: '#b8b8b8', base: '#ffffff' },
    black: { primary: '#373737', secondary: '#373737', accent: '#373737', base: '#000000' },
    luxury: { primary: '#ffffff', secondary: '#152747', accent: '#513448', base: '#09090b' },
    dracula: { primary: '#ff79c6', secondary: '#bd93f9', accent: '#ffb86c', base: '#282a36' },
    cmyk: { primary: '#179299', secondary: '#e900ff', accent: '#fbe000', base: '#ffffff' },
    autumn: { primary: '#8c0327', secondary: '#d85251', accent: '#d59b6a', base: '#f1f1f1' },
    business: { primary: '#1c4e80', secondary: '#7c909a', accent: '#ea6947', base: '#ffffff' },
    acid: { primary: '#ff00f4', secondary: '#ff7400', accent: '#bfff00', base: '#fafafa' },
    lemonade: { primary: '#519903', secondary: '#e9e92e', accent: '#bb9400', base: '#ffffff' },
    night: { primary: '#38bdf8', secondary: '#818cf8', accent: '#f471b5', base: '#0f1729' },
    coffee: { primary: '#db924b', secondary: '#6f4331', accent: '#10576d', base: '#20161f' },
    winter: { primary: '#047aed', secondary: '#463aa2', accent: '#c148ac', base: '#ffffff' },
  };

  return defaults[themeName] || { primary: '#000000', secondary: '#000000', accent: '#000000', base: '#ffffff' };
}

// Whether a theme counts as "dark" is derived from its actual base-100
// colour's luminance rather than a hand-maintained name list, which drifts
// out of sync the moment a theme is added or its colours change (TE-57 --
// TemplarEdgeDark was missing from the old list entirely; rosey/
// springgarden/citrus were wrongly listed as dark).
export function isColorDark(color: string): boolean {
  // Newer daisyUI themes (v5) define base-100 as oklch(L% C H) rather than
  // hex -- the L channel is already a perceptual lightness, 0% = black,
  // 100% = white, so it's a direct answer, not something to convert.
  const oklchMatch = color.match(/^oklch\(\s*([\d.]+)%/i);
  if (oklchMatch) return parseFloat(oklchMatch[1]) < 50;

  const clean = color.replace('#', '');
  if (clean.length !== 6) return false;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

export function isThemeDark(themeName: string): boolean {
  return isColorDark(getThemeColors(themeName).base);
}

// Theme Settings retains the existing catalogue order: TE/CO custom themes
// first, then DaisyUI's built-ins.
export function getThemeNames(): string[] {
  return [...Object.keys(customThemeColors), ...builtInThemeNames];
}

export const setTheme = (theme: string) => {
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem('theme', theme);
};

// DaisyUI 5 renamed every CSS variable to a full colour value under
// --color-* (e.g. --color-primary), dropping the old --p/--pf/--pc raw
// OKLCH-triplet scheme entirely -- including the *-focus variants (--pf,
// --sf, --af, --nf), which v5 has no equivalent for at all. Syncfusion
// theming below falls back to the base colour for those rather than
// inventing a new darken/lighten behaviour.
export const getCurrentThemeColors = () => {
  const root = document.documentElement;
  const computedStyle = getComputedStyle(root);
  const v = (name: string) => computedStyle.getPropertyValue(name);
  const primary = v('--color-primary');
  const secondary = v('--color-secondary');
  const accent = v('--color-accent');
  const neutral = v('--color-neutral');
  return {
    primary,
    primaryFocus: primary,
    primaryContent: v('--color-primary-content'),
    secondary,
    secondaryFocus: secondary,
    secondaryContent: v('--color-secondary-content'),
    accent,
    accentFocus: accent,
    accentContent: v('--color-accent-content'),
    neutral,
    neutralFocus: neutral,
    neutralContent: v('--color-neutral-content'),
    base100: v('--color-base-100'),
    base200: v('--color-base-200'),
    base300: v('--color-base-300'),
    baseContent: v('--color-base-content'),
    info: v('--color-info'),
    infoContent: v('--color-info-content'),
    success: v('--color-success'),
    successContent: v('--color-success-content'),
    warning: v('--color-warning'),
    warningContent: v('--color-warning-content'),
    error: v('--color-error'),
    errorContent: v('--color-error-content'),
  };
};

export const syncfusionTheme = (theme: string) => {
  const colors = getCurrentThemeColors();
  return {
    textColor: colors.baseContent,
    backgroundColor: colors.base100,
    borderColor: colors.base200,
    tooltipBackground: colors.secondary,
    tooltipText: colors.secondaryContent,
    tooltipBorder: colors.secondary,
    tooltipArrow: colors.secondary,
    buttonBackground: colors.primary,
    buttonText: colors.primaryContent,
    buttonBorder: colors.primary,
    buttonHoverBackground: colors.primaryFocus,
    buttonHoverText: colors.primaryContent,
    buttonActiveBackground: colors.primaryFocus,
    buttonActiveText: colors.primaryContent,
    buttonFocusRing: colors.primary,
    dialogBackground: colors.base100,
    dialogText: colors.baseContent,
    dialogBorder: colors.base200,
    dialogHeaderBackground: colors.base200,
    dialogHeaderText: colors.baseContent,
    dialogShadow: colors.base300,
    dialogOverlay: colors.neutral + '80',
    gridHeaderBackground: colors.base200,
    gridHeaderText: colors.baseContent,
    gridCellBackground: colors.base100,
    gridCellText: colors.baseContent,
    gridAltRowBackground: colors.base200,
    gridHoverBackground: colors.base300,
    gridSelectedBackground: colors.primary,
    gridSelectedText: colors.primaryContent,
    gridBorder: colors.base300,
    menuBackground: colors.base100,
    menuText: colors.baseContent,
    menuHoverBackground: colors.base200,
    menuHoverText: colors.baseContent,
    menuActiveBackground: colors.primary,
    menuActiveText: colors.primaryContent,
    menuBorder: colors.base200,
    dropdownBackground: colors.base100,
    dropdownText: colors.baseContent,
    dropdownBorder: colors.base200,
    dropdownHoverBackground: colors.base200,
    dropdownHoverText: colors.baseContent,
    dropdownSelectedBackground: colors.primary,
    dropdownSelectedText: colors.primaryContent,
    calendarBackground: colors.base100,
    calendarText: colors.baseContent,
    calendarHeaderBackground: colors.base200,
    calendarHeaderText: colors.baseContent,
    calendarSelectedBackground: colors.primary,
    calendarSelectedText: colors.primaryContent,
    calendarTodayBackground: colors.secondary,
    calendarTodayText: colors.secondaryContent,
    calendarHoverBackground: colors.base200,
    calendarBorder: colors.base200,
    tabBackground: colors.base100,
    tabText: colors.baseContent,
    tabActiveBackground: colors.primary,
    tabActiveText: colors.primaryContent,
    tabHoverBackground: colors.base200,
    tabBorder: colors.base200,
    scrollbarThumb: colors.base300,
    scrollbarTrack: colors.base100,
    inputBackground: colors.base100,
    inputText: colors.baseContent,
    inputBorder: colors.base200,
    inputFocusBorder: colors.primary,
    inputPlaceholder: colors.baseContent + '80',
    inputDisabledBackground: colors.base200,
    inputDisabledText: colors.baseContent + '60',
    infoBackground: colors.info,
    infoText: colors.infoContent,
    successBackground: colors.success,
    successText: colors.successContent,
    warningBackground: colors.warning,
    warningText: colors.warningContent,
    errorBackground: colors.error,
    errorText: colors.errorContent,
    chartAxisLine: colors.base200,
    chartGridLine: colors.base200 + '40',
    chartLabelText: colors.baseContent,
    chartLegendText: colors.baseContent,
    chartSeriesColors: [
      colors.primary, colors.secondary, colors.accent,
      colors.info, colors.success, colors.warning,
    ],
    spinnerColor: colors.primary,
    progressBackground: colors.base200,
    progressFill: colors.primary,
    progressText: colors.primaryContent,
    treeNodeBackground: colors.base100,
    treeNodeText: colors.baseContent,
    treeNodeHoverBackground: colors.base200,
    treeNodeSelectedBackground: colors.primary,
    treeNodeSelectedText: colors.primaryContent,
    treeNodeExpandIcon: colors.baseContent,
  };
};
